import React, { useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { translate } from '../../../services/translate';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import DeleteModal from '../../../components/DeleteModal';
import IgnoredPersonModal from './IgnoredPersonModal';
import MyDataTable from '../../../components/MyDataTable';
import { useQuery } from '@tanstack/react-query';
import { facialService } from '../../../services/central-api/facial';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import DropdownButton from '../../../components/DropdownButton';
import { Pagination } from '../../../components/Pagination';

export default function IgnoredPeople({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<FacialParamList, 'IgnoredPeople'>>();
    const [filters, setFilters] = useFilters<FacialParamList['IgnoredPeople']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedIgnoredPerson, setSelectedIgnoredPerson] = useState<IgnoredPerson>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    async function deleteIgnoredPerson(ignoredPerson: IgnoredPerson) {
        try {
            await facialService.deleteIgnoredPerson(ignoredPerson.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsDeleteModalVisible(false);
            setSelectedIgnoredPerson(undefined);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    const { data, refetch } = useQuery({
        queryKey: ['getPaginatedIgnoredPeople', filters],
        queryFn: () => facialService.getPaginatedIgnoredPeople(filters),
        enabled: isFocused,
    });

    return <Container>
        <Header
            title={translate('ignoredDocumentList')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedIgnoredPerson(undefined);
                setModalVisible(true);
            }} />}

        />
        <Card>
            <DataTableHeader
                title={translate('documentMarkedToIgnore')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        selector: row => row.document ? row.document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : '',
                        grow: 1,
                        name: translate('document'),
                    }, {
                        selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                        grow: 1,
                        name: translate('registeredAt'),
                    }, {
                        button: true,
                        cell: row =>
                            <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedIgnoredPerson(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedIgnoredPerson(row);
                                        setIsDeleteModalVisible(true);
                                    },
                                }]}
                            />
                    }]}
                    data={data?.rows ?? []}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        <IgnoredPersonModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            ignoredPerson={selectedIgnoredPerson}
            onClose={() => {
                setSelectedIgnoredPerson(undefined);
                setModalVisible(false);
                refetch();
            }}
        />
        <DeleteModal
            onSubmit={async () => {
                try {
                    if (selectedIgnoredPerson) {
                        await deleteIgnoredPerson(selectedIgnoredPerson);
                    }
                } catch (err) {
                    console.error(err);
                }
            }}
            isModalVisible={isDeleteModalVisible}
            itemName={selectedIgnoredPerson?.document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')}
            setModalVisible={() => setIsDeleteModalVisible(false)}
        />
    </Container>;
}
